











































































import {
    Component,
    ComponentConfig,
    ComponentStyle,
    ComponentStyleDefinition,
    InjectComponent,
    ProductRecommendation,
    Prop,
    Recommendation,
    RecommendationCluster,
    RecommendationSettingsBuilder,
    SectionType,
    Vue,
    VueComponent,
    Watch,
    FlowStep,
    AdvisorNavigation,
} from "@zoovu/runner-browser-api";
    import {
        RecommendationConfiguration, SearchbarConfiguration,
        TopProductConfiguration
    } from "../configuration";

    interface AdditionalClusterData {
        classList: string;
        clusterHeadline: string;
        clusterSubHeadline: string;
    }

    type RecommendationClusterExtended = RecommendationCluster & AdditionalClusterData;

    @Component({})
    export default class AdviceView extends Vue {

        @ComponentStyle()
        componentStyle: ComponentStyleDefinition;

        @Prop()
        advice: Recommendation;

        @Prop()
        sectionType: SectionType;

        @Prop()
        navigation: AdvisorNavigation;

        @Prop()
        filters: FlowStep[];

        @InjectComponent("TopProductView")
        TopProductView: VueComponent;

        @InjectComponent("ProductRecommendationView")
        productRecommendationView: VueComponent;

        @InjectComponent("AdviceNavigationView")
        adviceNavigationView: VueComponent;

        @InjectComponent("SortView")
        sortView: VueComponent;

        @InjectComponent("FilterPanelView")
        filterPanelView: VueComponent;

        @InjectComponent("AdviceSearchView")
        adviceSearchView: VueComponent;

        @ComponentConfig(RecommendationConfiguration)
        configuration: RecommendationConfiguration;

        @ComponentConfig(TopProductConfiguration)
        topProductConfiguration: TopProductConfiguration;

        @ComponentConfig(SearchbarConfiguration)
        searchbarConfiguration: SearchbarConfiguration;

        get shouldShowSearchBar(): boolean {
            if (this.searchbarConfiguration) {
                if (this.sectionType === SectionType.QUESTIONNAIRE) {
                    return this.searchbarConfiguration.showOnQuestionnaireSection;
                }

                if (this.sectionType === SectionType.RESULTS_PAGE) {
                    return this.searchbarConfiguration.showOnResultsSection;
                }
            }

            return false;
        }

        get filterWrapperClass(): Record<string, unknown> {
            return {
                'products-filters-wrapper': this.canRenderFilters,
            };
        }

        get canRenderFilters(): boolean {
            return this.filters
                && this.filters.length > 0
                && this.advice.hasProducts
                && this.sectionType === SectionType.RESULTS_PAGE;
        }

        get currentRecommendedProductsNumber() {
            if (this.sectionType === "QUESTIONNAIRE") {
                return this.configuration.numberOfProductsPerQuestionnairePage;
            } else if (this.sectionType === "RESULTS_PAGE") {
                return this.configuration.numberOfProductsPerResultsPage;
            } else {
                return 0;
            }
        }

        get currentTopProductsNumber(): number {
            if (this.sectionType === "QUESTIONNAIRE" && this.topProductConfiguration && this.topProductConfiguration.showTopProductsOnQuestionnairePages) {
                return this.topProductConfiguration.numberOfTopProductsPerQuestionnairePage;
            } else if (this.sectionType === "RESULTS_PAGE" && this.topProductConfiguration && this.topProductConfiguration.showTopProductsOnResultsPages) {
                return this.topProductConfiguration.numberOfTopProductsPerResultsPage;
            } else {
                return 0;
            }
        }

        get topProducts(): ProductRecommendation[] {
            const currentPage = this.advice.currentPage;
            const clusters = (currentPage && currentPage.pageNumber === 0 && currentPage.clusters) || [];
            const fullMatchesCluster = clusters.find(c => c.clusterNumber === 0);
            const fullMatches = fullMatchesCluster ? fullMatchesCluster.products : [];
            return fullMatches.slice(0, this.currentTopProductsNumber);
        }

        get currentPageClusters(): ReadonlyArray<RecommendationCluster> {
            if (!this.currentTopProductsNumber || this.advice.currentPage.pageNumber !== 0) {
                return this.advice.currentPage.clusters;
            }

            const topProductsNumber: number = this.currentTopProductsNumber;
            let bestProductCount: number = 0;
            let allProductCount: number = 0;

            return this.advice.currentPage.clusters.reduce((clusters, currentCluster) => {
                if (!currentCluster.products.length) return clusters;
                if (currentCluster.clusterNumber === 0) bestProductCount += currentCluster.products.length;
                allProductCount += currentCluster.products.length;
                if (topProductsNumber >= allProductCount) {
                    const topProducts = this.topProducts && this.topProducts.map((product) => product.productId);
                    const filteredProducts = currentCluster.products.filter((product) => !topProducts.includes(product.productId));
                    clusters.push({...currentCluster, products: filteredProducts});
                }
                if (allProductCount > topProductsNumber) {
                    const sliceCount = allProductCount - Math.min(topProductsNumber, bestProductCount);
                    const newProductsList: ReadonlyArray<ProductRecommendation> = currentCluster.products.filter((product, index, products) => index >= products.length - sliceCount);
                    clusters.push({...currentCluster, products: newProductsList});
                }
                return clusters;
            }, []);
        }

        get currentPageClustersExtended(): ReadonlyArray<RecommendationClusterExtended> {
            return this.currentPageClusters && this.currentPageClusters.map(cluster => ({
                ...cluster,
                ...this.getAdditionalDataForCluster(cluster.clusterNumber)
            }));
        }

        get additionalDataForPerfectMatchesCluster(): AdditionalClusterData {
            return {
                classList: 'cluster-perfect-matches',
                clusterHeadline: this.$t('message-result-first-cluster-header') as string,
                clusterSubHeadline: this.$t('message-result-first-cluster-subheader') as string
            }
        }

        get additionalDataForAlternativesCluster(): AdditionalClusterData {
            return {
                classList: 'cluster-alternatives',
                clusterHeadline: this.$t('message-result-first-cluster-header-alternatives') as string,
                clusterSubHeadline: this.$t('message-result-first-cluster-subheader-alternatives') as string
            }
        }

        get hasSubHeaderTop(): boolean {
            return this.$t('message-result-top-product-subheader') !== '';
        }

        @Watch("configuration", {deep: true})
        requestUpdate() {
            this.setVisualSettings();
            this.requestAdviceUpdate(true);
        }

        mounted(): void {
            this.setVisualSettings();
            this.requestAdviceUpdate(true);
            this.$root.$refs.recommendationView = this.$refs.recommendationView;
        }

        getAdditionalDataForCluster(clusterNumber: number): AdditionalClusterData {
            if(clusterNumber === 0) {
                return this.additionalDataForPerfectMatchesCluster;
            }
            return this.additionalDataForAlternativesCluster;
        }

        setVisualSettings(): void {
            const recommendationSettingsBuilder = new RecommendationSettingsBuilder()
                .withNumberOfProductsForSection(SectionType.QUESTIONNAIRE, {
                    numberOfProductsPerPage: this.configuration.numberOfProductsPerQuestionnairePage,
                    numberOfTopProductsPerPage: this.resolveTopProductsNumber(SectionType.QUESTIONNAIRE),
                    isQueryVisible: this.searchbarConfiguration.showOnQuestionnaireSection
                })
                .withNumberOfProductsForSection(SectionType.RESULTS_PAGE, {
                    numberOfProductsPerPage: this.configuration.numberOfProductsPerResultsPage,
                    numberOfTopProductsPerPage: this.resolveTopProductsNumber(SectionType.RESULTS_PAGE),
                    isQueryVisible: this.searchbarConfiguration.showOnResultsSection
                });

            if (!this.configuration.showInitialRecommendation) {
                recommendationSettingsBuilder.withInitialRecommendationDisabled();
            }

            this.advice.setRecommendationSettings(recommendationSettingsBuilder.build());
        }

        resolveTopProductsNumber(sectionType: SectionType): number {
            if (this.topProductConfiguration) {
                if (sectionType === SectionType.QUESTIONNAIRE) {
                    if (this.topProductConfiguration.showTopProductsOnQuestionnairePages) {
                        return this.topProductConfiguration.numberOfTopProductsPerQuestionnairePage;
                    }
                }

                if (sectionType === SectionType.RESULTS_PAGE) {
                    if (this.topProductConfiguration.showTopProductsOnResultsPages) {
                        return this.topProductConfiguration.numberOfTopProductsPerResultsPage;
                    }
                }
            }
            return 0;
        }

        requestAdviceUpdate(shouldRunEvenIfPageIsAlreadyLoaded: boolean): void {
            if (shouldRunEvenIfPageIsAlreadyLoaded || !this.advice.currentPage) {
                this.advice.requestUpdate();
            }
        }

        onResetFilters(): void {
            this.$emit("reset-filters");
        }
    }
